import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import client from 'api/environment';
import GET_ORDERS_COUNT from 'api/queries/GET_ORDERS_COUNT';
import GET_ORDERS_STATS from 'api/queries/GET_ORDERS_STATS';
import { STATUS_COLORS } from 'constants/statuses';
import DuplicateSvg from 'images/v5/icons/copy-01.svg';
import EditSvg from 'images/v5/icons/edit-02.svg';
import FileIcon from 'images/v5/icons/file-04.svg';
import ComponentsSvg from 'images/v5/icons/menu/components.svg';
import ManufacturersSvg from 'images/v5/icons/menu/manufacturers.svg';
import OrderOperatorsSvg from 'images/v5/icons/menu/order-operators.svg';
import OrdersSvg from 'images/v5/icons/menu/orders.svg';
import SpecificationsSvg from 'images/v5/icons/menu/specifications.svg';
import SuppliersSvg from 'images/v5/icons/menu/suppliers.svg';
import WarehouseReleasesSvg from 'images/v5/icons/menu/warehouse-releases.svg';
import PlusSvg from 'images/v5/icons/plus.svg';
import LoopSvg from 'images/v5/icons/search-md.svg';
import Button from 'layout/atoms/Button/Button';
import Card from 'layout/atoms/Card/Card';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Header from 'layout/atoms/Header/Header';
import Input from 'layout/atoms/Input/Input';
import Label from 'layout/atoms/Label';
import Loader from 'layout/atoms/Loader/Loader';
import Pie from 'layout/atoms/Pie';
import StatsCard from 'layout/atoms/StatsCard/StatsCard';
import TabsButton from 'layout/atoms/TabsButton/TabsButton';
import Grid from 'layout/molecules/Grid/Grid';
import PercentageBar from 'layout/molecules/PercentageBar';
import { useAppDispatch } from 'store/index';
import { clearBreadcrumbs, openModal, setBreadcrumbs } from 'store/layoutSlice';
import { addOrder } from 'store/ordersSlice';
import { fetchSeasons, selectSeasons, selectSeasonsStatus } from 'store/parameters/seasonsSlice';
import { addSpecification } from 'store/specificationDetailsSlice';
import { fetchRootFolder, selectRootFolder } from 'store/specificationsFoldersSlice';
import { addRelease } from 'store/warehouseReleasesSlice';
import Dashboard from 'templates/Dashboard/Dashboard';
import { getMenuLabel } from 'utils/getMenuItem';
function DashboardPage() {
    var _a, _b, _c, _d;
    const { t } = useTranslation(['dashboard', 'common', 'orders', 'warehouse_releases']);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const rootFolder = useSelector(selectRootFolder).rootFolderId;
    const [specsGroup, setSpecsGroup] = useState('recent');
    const seasons = useSelector(selectSeasons);
    const seasonsStatus = useSelector(selectSeasonsStatus).fetch;
    const [statsLoading, setStatsLoading] = useState(false);
    const [ordersStats, setOrdersStats] = useState({
        delayed: [],
        planned: [],
    });
    const [ordersTotalCount, setOrdersTotalCount] = useState(null);
    const delayedCount = (_b = (_a = ordersStats.delayed) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
    const plannedCount = (_d = (_c = ordersStats.planned) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0;
    const doneCount = 0;
    const overlapCount = useMemo(() => {
        const delayedSet = new Set(ordersStats.delayed || []);
        return (ordersStats.planned || []).filter((id) => delayedSet.has(id)).length;
    }, [ordersStats.delayed, ordersStats.planned]);
    const plannedOnlyCount = Math.max(plannedCount - overlapCount, 0);
    const fallbackUniqueTotal = useMemo(() => {
        const unique = new Set([...(ordersStats.delayed || []), ...(ordersStats.planned || [])]);
        return unique.size + doneCount;
    }, [doneCount, ordersStats.delayed, ordersStats.planned]);
    const statsCards = useMemo(() => {
        const total = ordersTotalCount !== null && ordersTotalCount !== void 0 ? ordersTotalCount : 0; // must use ordersCount from API to avoid double-counting overlapping statuses
        const safeTotal = total || 1; // avoid 0 total for pie
        return [
            {
                label: 'STATUS_ERROR',
                color: 'alert',
                pieColor: STATUS_COLORS.error,
                value: delayedCount,
                allCount: safeTotal,
            },
            {
                label: 'STATUS_IN_PROGRESS',
                color: 'info',
                pieColor: STATUS_COLORS['in-progress'],
                value: plannedCount,
                allCount: safeTotal,
            },
            {
                label: 'STATUS_DONE',
                color: 'success',
                pieColor: STATUS_COLORS.done,
                value: 0,
                allCount: safeTotal,
            },
        ];
    }, [delayedCount, ordersTotalCount, plannedCount]);
    const workingSeasonId = useMemo(() => {
        const workingSeason = seasons === null || seasons === void 0 ? void 0 : seasons.find((season) => season.workingSeason);
        return (workingSeason === null || workingSeason === void 0 ? void 0 : workingSeason.id) || null;
    }, [seasons]);
    const percentageBarSegments = useMemo(() => {
        const total = ordersTotalCount !== null && ordersTotalCount !== void 0 ? ordersTotalCount : fallbackUniqueTotal;
        // Business rule: delayed implies planned; treat delayed as the overlapping subset
        const delayedValue = overlapCount;
        const plannedValue = plannedOnlyCount;
        const doneValue = doneCount;
        const used = delayedValue + plannedValue + doneValue;
        const otherValue = Math.max(total - used, 0);
        return [
            {
                id: 'done',
                value: doneValue,
                color: STATUS_COLORS.done,
                label: t('orders:STATUS_DONE'),
            },
            {
                id: 'in-progress',
                value: plannedValue,
                color: STATUS_COLORS['in-progress'],
                label: t('orders:STATUS_ONLY_IN_PROGRESS'),
            },
            {
                id: 'error',
                value: delayedValue,
                color: STATUS_COLORS.error,
                label: t('orders:STATUS_ERROR'),
            },
            {
                id: 'other',
                value: otherValue,
                color: '#D4D8DE',
                label: t('orders:STATUS_AWAITING'),
            },
        ];
    }, [doneCount, fallbackUniqueTotal, ordersTotalCount, plannedOnlyCount, overlapCount, t]);
    const specs = {
        recent: [
            { id: 1, name: 'LCKZ0341033588' },
            { id: 2, name: 'MCKZ0401033588' },
            { id: 3, name: 'MJXA0020013588' },
            { id: 4, name: 'LJXA0424013588 PEARL' },
            { id: 5, name: 'LCKZ0586033588' },
        ],
        favs: [
            { id: 6, name: 'LCKZ0223020248' },
            { id: 7, name: 'LJXA0151010358' },
            { id: 8, name: 'MJXA0032013588 M JAPAN' },
            { id: 9, name: 'MJXA0032012389' },
            { id: 10, name: 'MJXA0129010248 POPRAWIONE' },
        ],
        incomplete: [
            { id: 11, name: 'MJXA0063010528', incomplete: true },
            { id: 12, name: 'LJXA0151010358 Z PUCHEM', incomplete: true },
            { id: 13, name: 'MJXA0126010248 POP I', incomplete: true },
            { id: 14, name: '-', incomplete: true },
            { id: 15, name: 'MJXA0126010248 POP II', incomplete: true },
        ],
    };
    useEffect(() => {
        const label = getMenuLabel('/dashboard', t);
        if (label) {
            dispatch(setBreadcrumbs([{ to: '/dashboard', name: label }]));
        }
        return () => {
            dispatch(clearBreadcrumbs());
        };
    }, [dispatch, t]);
    useEffect(() => {
        dispatch(fetchRootFolder());
    }, [dispatch]);
    useEffect(() => {
        if (seasonsStatus === 'idle') {
            dispatch(fetchSeasons());
        }
    }, [dispatch, seasonsStatus]);
    useEffect(() => {
        if (seasonsStatus === 'succeeded' || seasonsStatus === 'failed') {
            setStatsLoading(true);
            Promise.allSettled([
                client.query({
                    query: GET_ORDERS_STATS,
                    variables: {
                        statuses: null,
                        seasonId: workingSeasonId,
                    },
                    fetchPolicy: 'network-only',
                }),
                client.query({
                    query: GET_ORDERS_COUNT,
                    variables: {
                        statuses: null,
                        seasonId: workingSeasonId,
                    },
                    fetchPolicy: 'network-only',
                }),
            ])
                .then(([statsResult, ordersResult]) => {
                var _a, _b, _c, _d, _e, _f;
                if (statsResult.status === 'fulfilled') {
                    const stats = (_b = (_a = statsResult.value.data) === null || _a === void 0 ? void 0 : _a.me) === null || _b === void 0 ? void 0 : _b.ordersStats;
                    setOrdersStats({
                        delayed: (_c = stats === null || stats === void 0 ? void 0 : stats.delayedOrdersIds) !== null && _c !== void 0 ? _c : [],
                        planned: (_d = stats === null || stats === void 0 ? void 0 : stats.plannedOrdersIds) !== null && _d !== void 0 ? _d : [],
                    });
                }
                else {
                    setOrdersStats({
                        delayed: [],
                        planned: [],
                    });
                }
                if (ordersResult.status === 'fulfilled') {
                    const total = (_f = (_e = ordersResult.value.data) === null || _e === void 0 ? void 0 : _e.me) === null || _f === void 0 ? void 0 : _f.ordersCount;
                    setOrdersTotalCount(typeof total === 'number' ? total : null);
                }
                else {
                    setOrdersTotalCount(null);
                }
            })
                .finally(() => setStatsLoading(false));
        }
    }, [seasonsStatus, workingSeasonId]);
    const handleAddSpecification = () => {
        dispatch(addSpecification({ folderId: rootFolder })).then((response) => {
            var _a;
            return ((_a = response.payload) === null || _a === void 0 ? void 0 : _a.id) &&
                dispatch(openModal({ name: 'specificationCreator', data: { id: response.payload.id } }));
        });
    };
    const handleAddOrder = useCallback(() => {
        const action = dispatch(addOrder());
        action.unwrap().then((response) => {
            navigate(`/orders/creator/${response.id}`);
        });
    }, [dispatch, navigate]);
    const handleAddWarehouseRelease = () => {
        const action = dispatch(addRelease());
        action.unwrap().then((response) => {
            navigate(`/warehouse-releases/${response.id}`);
        });
    };
    const handleAddComponent = () => {
        dispatch(openModal({ name: 'componentCreator' }));
    };
    const handleAddSupplier = () => {
        dispatch(openModal({ name: 'supplierCreator' }));
    };
    const handleAddOrderOperator = () => {
        dispatch(openModal({ name: 'orderOperatorCreator' }));
    };
    const handleAddManufacturer = () => {
        dispatch(openModal({ name: 'manufacturerCreator' }));
    };
    return (React.createElement(Dashboard, { headerMain: React.createElement(React.Fragment, null,
            React.createElement(Input, { leftIcon: React.createElement(LoopSvg, null), placeholder: t('common:search'), disabled: true }),
            React.createElement(Dropdown, { position: "bottomRight", trigger: React.createElement(Button, { leftIcon: React.createElement(PlusSvg, null) }, t('CREATE_BUTTON_LABEL')) },
                React.createElement(Button, { align: "left", color: "inverse", leftIcon: React.createElement(SpecificationsSvg, null), onClick: handleAddSpecification }, t('CREATE_SPECIFICATION')),
                React.createElement("hr", null),
                React.createElement(Button, { align: "left", color: "inverse", leftIcon: React.createElement(OrdersSvg, null), onClick: handleAddOrder }, t('CREATE_ORDER')),
                React.createElement(Button, { align: "left", color: "inverse", leftIcon: React.createElement(WarehouseReleasesSvg, null), onClick: handleAddWarehouseRelease }, t('CREATE_WAREHOUSE_RELEASE')),
                React.createElement("hr", null),
                React.createElement(Button, { align: "left", color: "inverse", leftIcon: React.createElement(ComponentsSvg, null), onClick: handleAddComponent }, t('CREATE_COMPONENT')),
                React.createElement(Button, { align: "left", color: "inverse", leftIcon: React.createElement(SuppliersSvg, null), onClick: handleAddSupplier }, t('CREATE_SUPPLIER')),
                React.createElement(Button, { align: "left", color: "inverse", leftIcon: React.createElement(OrderOperatorsSvg, null), onClick: handleAddOrderOperator }, t('CREATE_ORDER_OPERATOR')),
                React.createElement(Button, { align: "left", color: "inverse", leftIcon: React.createElement(ManufacturersSvg, null), onClick: handleAddManufacturer }, t('CREATE_MANUFACTURER')))) },
        React.createElement(Grid, { gap: 16, vp: 20, hp: 24 },
            React.createElement(Grid, { columns: 3, gap: 24 }, statsCards.map((item) => (React.createElement(StatsCard, { key: item.label, color: item.color, label: t(`orders:${item.label}`), number: item.value || 0, aside: React.createElement(Pie, { value: item.value || 0, total: item.allCount || 0, color: item.pieColor, size: "m" }) })))),
            React.createElement(Grid, { columns: "7_5", gap: 24 },
                React.createElement(Grid, { columns: 1, gap: 24 }, statsLoading ? (React.createElement(Card, { noHover: true },
                    React.createElement(Label, { size: "m", weight: "strong" }, t('SPECIFICATION_PROGRESS_BAR_HEADER')),
                    React.createElement(Loader, null))) : (React.createElement(PercentageBar, { title: t('SPECIFICATION_PROGRESS_BAR_HEADER'), segments: percentageBarSegments }))),
                React.createElement(Grid, { columns: 1, gap: 24 },
                    React.createElement(Card, { noHover: true },
                        React.createElement(Grid, { columns: 1, gap: 12 },
                            React.createElement(Header, { size: "m", aside: React.createElement(TabsButton, { tabs: [
                                        { id: 'recent', label: t('SPECS_RECENT') },
                                        { id: 'favs', label: t('SPECS_FAVOURITES') },
                                        { id: 'incomplete', label: t('SPECS_INCOMPLETE') },
                                    ], activeTab: specsGroup, onClick: (e) => setSpecsGroup(e) }) }, t('SPECS_HEADER')),
                            React.createElement(Grid, { columns: 1, gap: 12 }, specs[specsGroup].map((spec) => (React.createElement(Card, { key: spec.id },
                                React.createElement(Header, { aside: React.createElement(Dropdown, null,
                                        React.createElement(Button, { theme: "ghost", align: "left", leftIcon: React.createElement(EditSvg, null), disabled: true }, t('common:edit')),
                                        React.createElement(Button, { theme: "ghost", align: "left", leftIcon: React.createElement(DuplicateSvg, null), disabled: true }, t('common:duplicate_as_draft')),
                                        React.createElement(Button, { theme: "ghost", align: "left", leftIcon: React.createElement(OrdersSvg, null), disabled: true }, t('SPECS_NEW_ORDER')),
                                        React.createElement(Button, { theme: "ghost", align: "left", leftIcon: React.createElement(WarehouseReleasesSvg, null), disabled: true }, t('SPECS_NEW_WAREHOUSE_RELEASE'))) },
                                    React.createElement(FileIcon, null),
                                    spec.name))))))))))));
}
export default memo(DashboardPage);
